import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RECONCILE_ERROR_CODES, resolveCaptureError } from './captureErrors';
import { ADMIN_ORDERS_KEY } from '../../../api/admin-orders';
import { postReconcileCapture, type AdminReconcileResolution } from '../../../api/admin-payments';
import { ADMIN_PAYOUTS_KEY } from '../../../api/admin-payouts';
import { publishToast, type ToastSeverity } from '../../../components/feedback';

/**
 * AUT-223 — reconcile orchestration for the order detail's Платёж tab.
 *
 * Mirrors `useCapturePayment` (inline error, invalidate-only) with ONE
 * difference that matters: a 200 here is not automatically good news. The
 * endpoint answers with a `resolution`, and three of its four values mean the
 * operator still has work to do. So the success toast is keyed off the
 * resolution rather than off the HTTP status — reporting
 * "no_provider_record" as a plain green "готово" would tell an operator the
 * problem is solved when the provider has never heard of the payment.
 */

/** Resolution → toast severity. Only a confirmed capture is unambiguously good. */
const RESOLUTION_SEVERITY: Record<AdminReconcileResolution, ToastSeverity> = {
  captured: 'success',
  // The hold is live again and can be captured — fine, but it's a state
  // change the operator must notice, not a completion.
  reverted_to_authorized: 'info',
  no_provider_record: 'warning',
  unresolved: 'warning',
};

export interface UseReconcileCaptureResult {
  busy: boolean;
  /** Resolved server-error text, `null` when nothing is pending. */
  error: string | null;
  reset: () => void;
  /** Run the reconcile; resolves true when the request itself succeeded. */
  run: (paymentId: string) => Promise<boolean>;
}

export const useReconcileCapture = (): UseReconcileCaptureResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => setError(null), []);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY }),
      queryClient.invalidateQueries({ queryKey: ADMIN_PAYOUTS_KEY }),
    ]);
  };

  const run = async (paymentId: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const result = await postReconcileCapture(paymentId);
      await invalidate();
      publishToast({
        severity: RESOLUTION_SEVERITY[result.resolution],
        durationMs: 8000,
        message: t(`orders.capture.reconcileToast.${result.resolution}`, {
          providerStatus: result.providerStatus ?? t('orders.capture.noProviderStatus'),
        }),
      });
      return true;
    } catch (err: unknown) {
      const text = resolveCaptureError(err, RECONCILE_ERROR_CODES);
      setError(text.kind === 'key' ? t(text.key, text.params ?? {}) : text.text);
      await invalidate();
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, reset, run };
};
